/* eslint-disable class-methods-use-this */
import {
  ACCOUNT_CATEGORY,
  ACCOUNT_TYPE,
  AccountImportedChannelData,
  BasePopupChannelData,
  // BillboardEvent,
  BROADCAST_CHANNELS,
  BROADCAST_CHANNELS_MSGS,
  broadcastChannelOptions,
  Contact,
  ContactPayload,
  DEFAULT_PREFERENCES,
  NetworkChangeChannelData,
  PaymentTransaction,
  PopupData,
  PopupStoreChannel,
  ProviderConfig,
  randomId,
  SelectedAddresssChangeChannelData,
  significantDigits,
  THEME,
  TX_EVENTS,
  UserInfo,
} from "@toruslabs/base-controllers";
import { BroadcastChannel } from "@toruslabs/broadcast-channel";
import { BasePostMessageStream } from "@toruslabs/openlogin-jrpc";
import { LOGIN_PROVIDER_TYPE, storageAvailable } from "@toruslabs/openlogin-utils";
import {
  ChainNetwork,
  ChainNetworkWithRequiredFields,
  CHAINS,
  ExtendedAddressPreferences,
  FormattedTransactionActivity,
  getDefaultXRPLChainConfig,
  MESSAGE_EVENTS,
  NftDetails,
  NftOffer,
  SupportedToken,
  SupportedTokens,
  Trustline,
  UserCustomFungibleToken,
  XRPLNetwork,
  XrplProviderConfig,
} from "@toruslabs/xrpl-controllers";
// import Torus from "@toruslabs/torus.js";
import BigNumber from "bignumber.js";
import { cloneDeep, merge, omit } from "lodash-es";
import log from "loglevel";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import config from "@/config";
import TorusController, { DEFAULT_CONFIG, DEFAULT_STATE } from "@/controllers/TorusController";
import installStorePlugin from "@/plugins/persistPlugin";
import { CONTROLLER_MODULE_KEY, LOCAL_STORAGE_KEY, STATUS, TorusControllerState } from "@/utils/enums";
import { isMain, setTheme } from "@/utils/helpers";
import { NAVBAR_MESSAGES } from "@/utils/messages";

import OpenLoginFactory from "../auth/OpenLoginHandler";
import store from "../store";
import { addToast } from "./app";

// export const nodeDetailManager = new NodeDetailManager({
//   network: process.env.VUE_APP_PROXY_NETWORK as ETHEREUM_NETWORK_TYPE,
//   proxyAddress: process.env.VUE_APP_PROXY_ADDRESS as string,
// });

// export const torusCore = new Torus();

const moduleName = `${CONTROLLER_MODULE_KEY}-${config.dappStorageKey || "wallet"}`;
@Module({
  name: moduleName,
  namespaced: true,
  dynamic: true,
  store,
})
class ControllerModule extends VuexModule {
  // All properties defined here must have a default value
  public torus = new TorusController({ config: DEFAULT_CONFIG, state: DEFAULT_STATE });

  public torusState: TorusControllerState = cloneDeep(DEFAULT_STATE);

  public instanceId = "";

  get selectedAddress(): string {
    return this.torus.selectedAddress;
  }

  get selectedPublicKey(): string {
    return this.torus.selectedPublicKey;
  }

  get selectedNetworkConfig(): XrplProviderConfig {
    return this.torusState.NetworkControllerState.providerConfig;
  }

  get explorerLink(): string {
    return `${this.selectedNetworkConfig.blockExplorerUrl}`;
  }

  get userExplorerLink(): string {
    return `${this.selectedNetworkConfig.blockExplorerUrl}/accounts/${this.selectedAddress}`;
  }

  get selectedChainId(): string {
    return this.torusState.NetworkControllerState.chainId;
  }

  get selectedNetworkDisplayName(): string {
    const network = this.torusState.NetworkControllerState.providerConfig.displayName;
    return network;
  }

  get selectedNetworkKeyName(): string {
    const network = this.torusState.NetworkControllerState.providerConfig.networkKey;
    return network;
  }

  get selectedNetworkTransactions(): (FormattedTransactionActivity | PaymentTransaction)[] {
    const pastTxs = this.selectedAccountPreferences.formattedPastTransactions.filter((txn) => txn.chainId === this.selectedChainId) ?? [];
    const paymentTxs = this.selectedAccountPreferences.paymentTx ?? [];
    return [...pastTxs, ...paymentTxs].sort((a, b) => {
      const aDate = "rawDate" in a ? a.rawDate : a.date;
      const bDate = "rawDate" in b ? b.rawDate : b.date;
      return new Date(bDate).valueOf() - new Date(aDate).valueOf();
    });
  }

  get selectedNetworkBlockExplorerUrl(): string {
    const network = this.torusState.NetworkControllerState.providerConfig.blockExplorerUrl;
    return network;
  }

  get userFiatBalanceString(): string {
    return `${this.userBalance} ${this.currentCurrency}`;
  }

  get currentCurrency(): string {
    return this.torusState.CurrencyControllerState.currentCurrency.toUpperCase();
  }

  get xrpPrice(): BigNumber {
    return new BigNumber(this.torusState.CurrencyControllerState.conversionRate);
  }

  get conversionRateString(): string {
    return `${this.torusState.CurrencyControllerState.conversionRate} ${this.currentCurrency}`;
  }

  get userInfo(): UserInfo {
    return this.torus.userInfo;
  }

  get userXrplBalance(): number {
    const balance = this.torusState.AccountTrackerState.accounts[this.torus.selectedAddress]?.balance || "0x0";
    const value = new BigNumber(balance);
    return value.toNumber();
  }

  get userBalance(): string {
    const pricePerToken = this.torusState.CurrencyControllerState.conversionRate;
    const value = new BigNumber(this.userXrplBalance).times(new BigNumber(pricePerToken));
    return significantDigits(value.toNumber(), false, 2).toString();
  }

  get userXrplBalanceFormatted(): string {
    return `${this.userXrplBalance} XRP`;
  }

  get userReservedBalance(): string {
    const balance = this.torusState.AccountTrackerState.accounts[this.torus.selectedAddress]?.reservedBalance || "0x0";
    const value = new BigNumber(balance).div(new BigNumber(10 ** 6));
    return value.toString();
  }

  get accounts(): { type: ACCOUNT_TYPE; balance: string; isActive: boolean; address: string; fiatBalanceStr: string; reserveValueStr: string }[] {
    const accounts = this.torusState.AccountTrackerState.accounts || {};
    const { selectedAddress } = this.torus;

    const getAccountReservedBalance = (address: string): BigNumber => {
      const balance = this.torusState.AccountTrackerState.accounts[address]?.reservedBalance || "0x0";
      const value = new BigNumber(balance).div(new BigNumber(10 ** 6));
      return value;
    };

    return Object.keys(accounts).map((address) => {
      const account = accounts[address];
      const fiatBal = significantDigits(new BigNumber(account.balance).times(this.xrpPrice), false, 2);
      const fiatBalanceStr = `${fiatBal.toString()} ${this.currentCurrency}`;
      const reserveValue = significantDigits(getAccountReservedBalance(address).times(this.xrpPrice), false, 2);
      const reserveValueStr = `${reserveValue.toString()} ${this.currentCurrency}`;

      return {
        type: account.type as string,
        balance: account.balance,
        fiatBalanceStr,
        isActive: address === selectedAddress,
        address,
        reserveValueStr,
      };
    });
  }

  get selectedAccountPreferences(): ExtendedAddressPreferences {
    const preferences = this.torus.getAccountPreferences(this.torus.selectedAddress);
    return preferences || { ...DEFAULT_PREFERENCES, formattedPastTransactions: [], fetchedPastTx: [], paymentTx: [] };
  }

  get contacts(): Contact[] {
    return [...this.selectedAccountPreferences.contacts];
  }

  get isDarkMode(): boolean {
    return this.selectedAccountPreferences.theme === "dark";
  }

  get availableChainConfigNetworks(): ChainNetwork[] {
    const { chainNetworks } = this.torusState.ChainConfigNetworksState;
    return chainNetworks;
  }

  get supportedTokens(): SupportedTokens | undefined {
    const { supportedTokens } = this.torusState.TokensInfoState;
    if (!supportedTokens) return undefined;

    return Object.entries(supportedTokens).reduce((obj, [issuer, tokensArr]) => {
      const arr: SupportedToken[] = [];
      (tokensArr as SupportedToken[]).forEach((token) => {
        // eslint-disable-next-line no-param-reassign
        token.price = new BigNumber(token.price);
        arr.push(token);
      });
      // eslint-disable-next-line no-param-reassign
      obj[issuer] = arr;
      return obj;
    }, {} as SupportedTokens);
  }

  get customFungibleTokens(): UserCustomFungibleToken[] {
    const { customFungibleTokens } = this.torusState.TokensInfoState;
    const address = this.selectedAddress;
    if (!customFungibleTokens) {
      return [];
    }
    return (customFungibleTokens[address] || []).map((token) => {
      return { ...token, price: new BigNumber(token.price) };
    });
  }

  get issuerAddressToNameMap(): Record<string, string> | undefined {
    return this.torusState.TokensInfoState.issuerAddressToNameMap;
  }

  get selectedAddressXrpBalance(): string {
    return this.torusState.AccountTrackerState.accounts[this.torus.selectedAddress]?.balance ?? "0";
  }

  get selectedAddressTokenBalances(): Trustline[] {
    return [...(this.torusState.TokensTrackerState.lines?.[this.torus.selectedAddress] ?? [])];
  }

  get selectedAddressPendingTokens(): FormattedTransactionActivity[] {
    return [...(this.torusState.TokensTrackerState.pendingLines?.[this.torus.selectedAddress] ?? [])];
  }

  get nfts(): NftDetails[] {
    const nfts = this.torus.getAccountNfts(this.torus.selectedAddress);
    // eslint-disable-next-line no-console
    console.log("nfts in state", nfts);
    return [...(nfts || [])];
  }

  get nftOffersSent(): NftOffer[] {
    return (
      this.torus
        .getAccountNftOffers(this.torus.selectedAddress)
        ?.filter((offer) => offer.from === this.torus.selectedAddress && offer.offerType === "sell") ?? []
    );
  }

  get nftOffersReceived(): NftOffer[] {
    return (
      this.torus
        .getAccountNftOffers(this.torus.selectedAddress)
        ?.filter((offer) => offer.to === this.torus.selectedAddress && offer.offerType === "sell") ?? []
    );
  }

  get crashReport(): boolean {
    return this.selectedAccountPreferences.crashReport || false;
  }

  get theme(): string {
    return this.selectedAccountPreferences.theme || "light";
  }

  get proxyUrl(): string {
    return this.torus.config.ChainConfigControllerConfig.proxy;
  }

  get isCustomNetwork(): boolean {
    return (
      this.selectedNetworkKeyName !== CHAINS.XRPL_MAINNET &&
      this.selectedNetworkKeyName !== CHAINS.XRPL_TESTNET &&
      this.selectedNetworkKeyName !== CHAINS.XRPL_DEVNET
    );
  }

  @Mutation
  public setInstanceId(instanceId: string) {
    this.instanceId = instanceId;
  }

  @Mutation
  public updateTorusState(state: TorusControllerState): void {
    this.torusState = { ...state };
  }

  @Action
  handleError(error: string): void {
    addToast({ type: STATUS.DANGER, message: error });
  }

  @Action
  handleSuccess(message: string): void {
    addToast({ type: "success", message: message || "" });
  }

  @Action
  public async setCrashReport(status: boolean): Promise<void> {
    const isSet = await this.torus.setCrashReport(status);
    if (isSet) {
      if (storageAvailable("localStorage")) {
        localStorage.setItem("torus-enable-crash-reporter", String(status));
      }
      this.handleSuccess(NAVBAR_MESSAGES.success.CRASH_REPORT_SUCCESS);
    } else {
      this.handleError(NAVBAR_MESSAGES.error.CRASH_REPORT_FAILED);
    }
  }

  @Action
  public async addContact(contactPayload: ContactPayload): Promise<void> {
    const isAdded = await this.torus.addContact(contactPayload);
    if (isAdded) {
      this.handleSuccess(NAVBAR_MESSAGES.success.ADD_CONTACT_SUCCESS);
    } else {
      this.handleError(NAVBAR_MESSAGES.error.ADD_CONTACT_FAILED);
    }
  }

  @Action
  public async deleteContact(contactId: number): Promise<void> {
    const isDeleted = await this.torus.deleteContact(contactId);
    if (isDeleted) {
      this.handleSuccess(NAVBAR_MESSAGES.success.DELETE_CONTACT_SUCCESS);
    } else {
      this.handleError(NAVBAR_MESSAGES.error.DELETE_CONTACT_FAILED);
    }
  }

  @Action
  public async setTheme(theme: THEME): Promise<void> {
    await this.torus.setTheme(theme);
  }

  @Action
  public async setCurrency(currency: string): Promise<void> {
    const isSet = await this.torus.setDefaultCurrency(currency);
    if (isSet) {
      this.handleSuccess(NAVBAR_MESSAGES.success.SET_CURRENCY_SUCCESS);
    } else {
      this.handleError(NAVBAR_MESSAGES.error.SET_CURRENCY_FAILED);
    }
  }

  @Action
  public async setLocale(locale: string): Promise<void> {
    const isSet = await this.torus.setLocale(locale);
    if (isSet) {
      this.handleSuccess(NAVBAR_MESSAGES.success.SET_LOCALE_SUCCESS);
    } else {
      this.handleError(NAVBAR_MESSAGES.error.SET_LOCALE_FAILED);
    }
  }

  // @Action
  // public async getBillBoardData(): Promise<BillboardEvent[]> {
  //   return this.torus.getBillboardData();
  // }

  @Action
  public toggleIframeFullScreen(): void {
    this.torus.toggleIframeFullScreen();
  }

  /**
   * Call once on refresh
   */
  @Action
  public async init({ state, origin }: { state?: Partial<TorusControllerState>; origin: string }): Promise<void> {
    this.torus.init({ config: DEFAULT_CONFIG, state: merge(this.torusState, state) });
    const addressState = this.torus.state.PreferencesControllerState?.identities?.[this.torus.selectedAddress];
    if (addressState?.theme) {
      setTheme(addressState?.theme === "dark");
    }
    this.torus.setOrigin(origin);
    this.torus.on("store", (state2: TorusControllerState) => {
      // log.info("store updated", state2);
      this.updateTorusState(state2);
    });
    // Good
    this.torus.on(TX_EVENTS.TX_UNAPPROVED, async ({ txMeta, req }) => {
      if (isMain) {
        this.torus.approveTransaction(txMeta.id);
      } else {
        await this.torus.handleTransactionPopup(txMeta.id, req);
      }
    });

    this.torus.on(MESSAGE_EVENTS.UNAPPROVED_MSG, async ({ messageParams, req }) => {
      if (!isMain) {
        await this.torus.handleSignMessagePopup(messageParams, req);
      }
    });
    this.torus.on("logout", () => {
      this.logout();
    });
    this.setInstanceId(randomId());
    if (!isMain) {
      const popupStoreChannel = new PopupStoreChannel({
        instanceId: this.instanceId,
        handleLogout: this.handleLogoutChannelMsg.bind(this),
        handleAccountImport: this.importAccount.bind(this),
        handleNetworkChange: (providerConfig: ProviderConfig) => this.setNetwork(providerConfig.chainId),
        handleSelectedAddressChange: this.setSelectedAccount.bind(this),
        handleThemeChange: this.handleThemeChange.bind(this),
      });
      popupStoreChannel.setupStoreChannels();
    }
  }

  @Action
  public setupCommunication(origin: string): void {
    log.info("setting up communication with", origin);
    const torusStream = new BasePostMessageStream({
      name: "iframe_torus",
      target: "embed_torus",
      targetWindow: window.parent,
      targetOrigin: origin,
    });

    const communicationStream = new BasePostMessageStream({
      name: "iframe_communication",
      target: "embed_communication",
      targetWindow: window.parent,
      targetOrigin: origin,
    });
    this.torus.setupUnTrustedCommunication(torusStream, origin);
    this.torus.setupCommunicationChannel(communicationStream, origin);
  }

  @Action
  async triggerLogin({ loginProvider, login_hint: loginHint }: { loginProvider: LOGIN_PROVIDER_TYPE; login_hint?: string }): Promise<void> {
    await this.torus.triggerLogin({ loginProvider, login_hint: loginHint });
  }

  @Action
  handleLogoutChannelMsg(): void {
    this.torus.handleLogout();
  }

  @Action
  async logout(): Promise<void> {
    const initialState = {
      ...cloneDeep(DEFAULT_STATE),
      NetworkControllerState: {
        ...cloneDeep(this.torus.state.NetworkControllerState),
      },
      ChainConfigNetworksState: {
        ...cloneDeep(this.torus.state.ChainConfigNetworksState),
      },
    };
    this.updateTorusState(initialState);
    const { origin } = this.torus;
    this.torus.init({
      config: {
        ...cloneDeep(DEFAULT_CONFIG),
      },
      state: { ...initialState },
    });

    const addressState = this.torus.state.PreferencesControllerState?.identities?.[this.torus.selectedAddress];
    setTheme(addressState?.theme === "dark");

    const openLoginHandlerInstance = await OpenLoginFactory.getInstance();
    await openLoginHandlerInstance.invalidateSession();
    this.torus.setOrigin(origin);
    const instanceId = new URLSearchParams(window.location.search).get("instanceId");
    if (instanceId) {
      const logoutChannel = new BroadcastChannel<PopupData<BasePopupChannelData>>(
        `${BROADCAST_CHANNELS.WALLET_LOGOUT_CHANNEL}_${instanceId}`,
        broadcastChannelOptions
      );
      logoutChannel.postMessage({
        data: {
          type: BROADCAST_CHANNELS_MSGS.LOGOUT,
        },
      });
      logoutChannel.close();
    }
  }

  @Action
  async refreshChainConfigNetworks() {
    await this.torus.refreshChainConfigNetworks();
  }

  @Action
  async createCustomXrpNetwork(chainConfig: Omit<ChainNetworkWithRequiredFields, "chainId">) {
    await this.torus.createCustomXrpNetwork(chainConfig);
  }

  @Action
  async updateCustomXrpNetwork(xrpNetwork: ChainNetworkWithRequiredFields) {
    await this.torus.updateCustomXrpNetwork(xrpNetwork);
  }

  @Action
  async deleteCustomXrpNetwork(chainId: string) {
    await this.torus.deleteCustomXrpNetwork(chainId);
  }

  @Action
  async saveCustomFungibleTokenDetails(fungibleToken: Omit<UserCustomFungibleToken, "public_address">) {
    const selectedAddr = this.selectedAddress;
    await this.torus.saveCustomFungibleTokenDetails({ ...fungibleToken, public_address: selectedAddr });
  }

  @Action
  async removeCustomFungibleToken(token: Omit<UserCustomFungibleToken, "public_address" | "network">) {
    const network = this.selectedNetworkKeyName;
    await this.torus.removeCustomFungibleToken({ ...token, public_address: this.selectedAddress, network });
  }

  @Action
  setNetwork(networkKey: string): void {
    // firstly, try to fetch the XrplChainConfig from the server
    let providerConfig: ChainNetwork = this.torus.getXrplChainConfig(networkKey);
    // if the server fetch failed or no networks avaialbles, we will use the default hard coded one
    if (!providerConfig) {
      // this is a fallback
      const defaultCfg = getDefaultXRPLChainConfig(networkKey as XRPLNetwork);
      if (!defaultCfg) {
        throw new Error(`Unknown network: ${networkKey}`);
      }
      providerConfig = defaultCfg;
    }

    // if the network is custom network, put the proxy
    if (providerConfig.isUserDefined) {
      providerConfig = { ...providerConfig, rpcTarget: `${this.torus.config.ChainConfigControllerConfig.proxy}/networks/${providerConfig.chainId}` };
    }

    this.torus.setNetwork(providerConfig);
    const instanceId = new URLSearchParams(window.location.search).get("instanceId");
    if (instanceId) {
      const networkChangeChannel = new BroadcastChannel<PopupData<NetworkChangeChannelData>>(
        `${BROADCAST_CHANNELS.WALLET_NETWORK_CHANGE_CHANNEL}_${instanceId}`,
        broadcastChannelOptions
      );
      networkChangeChannel.postMessage({
        data: {
          type: BROADCAST_CHANNELS_MSGS.NETWORK_CHANGE,
          network: providerConfig,
        },
      });
      networkChangeChannel.close();
    }
  }

  @Action
  async importAccount(privKey: string): Promise<void> {
    const address = await this.torus.addAccount(privKey, this.torus.userInfo, ACCOUNT_CATEGORY.IMPORTED);
    this.torus.setSelectedAccount(address);
    const instanceId = new URLSearchParams(window.location.search).get("instanceId");
    if (instanceId) {
      const accountImportChannel = new BroadcastChannel<PopupData<AccountImportedChannelData>>(
        `${BROADCAST_CHANNELS.WALLET_ACCOUNT_IMPORT_CHANNEL}_${instanceId}`,
        broadcastChannelOptions
      );
      accountImportChannel.postMessage({
        data: {
          type: BROADCAST_CHANNELS_MSGS.ACCOUNT_IMPORTED,
          privKey,
        },
      });
      accountImportChannel.close();
    }
  }

  @Action
  async setSelectedAccount(address: string) {
    this.torus.setSelectedAccount(address);
    const instanceId = new URLSearchParams(window.location.search).get("instanceId");
    if (instanceId) {
      const selectedAddressChannel = new BroadcastChannel<PopupData<SelectedAddresssChangeChannelData>>(
        `${BROADCAST_CHANNELS.WALLET_SELECTED_ADDRESS_CHANNEL}_${instanceId}`,
        broadcastChannelOptions
      );
      selectedAddressChannel.postMessage({
        data: {
          type: BROADCAST_CHANNELS_MSGS.SELECTED_ADDRESS_CHANGE,
          selectedAddress: address,
        },
      });
      selectedAddressChannel.close();
    }
    // refresh chain config networks
    await this.torus.refreshChainConfigNetworks();
    const currentSelectedNetwork = this.selectedNetworkKeyName;
    // if the newly selected address is not belong to the custom network
    if (!this.availableChainConfigNetworks.find((cfg) => cfg.networkKey === currentSelectedNetwork)) {
      // set to mainnet
      this.setNetwork(CHAINS.XRPL_MAINNET);
    }
  }

  @Action
  openWalletPopup(params: { path: string; action?: string }) {
    this.torus.showWalletPopup(params.path, this.instanceId, params.action);
  }

  @Action
  async handleThemeChange() {
    log.info("handleThemeChange");
  }

  @Action
  async checkIsAccountActivated(address: string): Promise<boolean> {
    return this.torus.checkIsAccountActivated(address);
  }

  @Action
  async fundAccountFromFaucet(address: string) {
    if (this.selectedNetworkKeyName !== CHAINS.XRPL_MAINNET) {
      await this.torus.fundAccountFromFaucet(address, this.selectedNetworkKeyName);
    }
  }
}

const module = getModule(ControllerModule);

installStorePlugin({
  key: moduleName,
  storage: LOCAL_STORAGE_KEY,
  saveState: (key: string, state: Record<string, unknown>, storage?: Storage) => {
    const requiredState = omit(state, [`${moduleName}.torus`]);
    storage?.setItem(key, JSON.stringify(requiredState));
  },
  restoreState: (key: string, storage?: Storage) => {
    const value = storage?.getItem(key);
    if (typeof value === "string") {
      // If string, parse, or else, just return
      const parsedValue = JSON.parse(value || "{}");
      return {
        [moduleName]: {
          torus: new TorusController({ config: DEFAULT_CONFIG, state: DEFAULT_STATE }),
          ...(parsedValue[moduleName] || {}),
        },
      };
    }
    return value || {};
  },
});

export default module;
